import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { useRouting, useScrollTop } from '@alycecom/hooks';
import { Alert } from '@mui/lab';

import RecipientActionWrapper from '../RecipientActionWrapper';
import { useShipping } from '../../hooks/useShipping';
import { getCountryId, getIsDonation, getProductId, getShipping } from '../../store/giftFlow/giftFlow.selectors';
import { setCountryId } from '../../store/giftFlow/giftFlow.actions';
import { GIFT_FLOW_SHIPPING_ID } from '../../../../constants/giftFlow.constants';
import useGiftStatuses from '../../hooks/useGiftStatuses';
import { MARKETPLACE_DONATE_ROUTE, MARKETPLACE_ITEMS_ROUTE } from '../../constants/marketplaceRoute.constants';
import usePageLoadEvent from '../../../../hooks/usePageLoadEvent';
import useTrackRecipient from '../../../../hooks/useTrackRecipient';
import { getShippingAddressFlow } from '../../store/recipient.selectors';
import { AddressOption, ShippingAddressFlow } from '../../store/shipping/shipping.types';
import { getRecipientState } from '../../../../store/users/users.selectors';
import PageTitle from '../PageTitle/PageTitle';
import { fetchShippingAddress } from '../../store/shipping/shipping.actions';
import { getGiftHashId } from '../../store/data/data.selectors';

import ShippingAddressForm from './ShippingAddressForm/ShippingAddressForm';
import RecipientInfo from './RecipientInfo/RecipientInfo';
import AddressSelectionForm from './AddressSelectionForm';

export interface IShippingPageProps {
  parentUrl: string;
}

const ShippingPage = ({ parentUrl }: IShippingPageProps): JSX.Element => {
  const { setActiveStatus, goNextStep } = useGiftStatuses();
  const go = useRouting();
  const trackEvent = useTrackRecipient();
  const dispatch = useDispatch();

  const hashId = useSelector(getGiftHashId);
  const recipient = useSelector(getRecipientState);
  const isDonation = useSelector(getIsDonation);
  const addressFlow = useSelector(getShippingAddressFlow);
  const productId = useSelector(getProductId);
  const countryId = useSelector(getCountryId);
  const shipping = useSelector(getShipping);

  const {
    submitShippingAddress,
    resetFieldError,
    selectShippingAddress,
    saveShippingAddress,
    shouldGoToTheNextStep,
  } = useShipping();

  const handleBackToMarketplace = useCallback(
    newCountryId => {
      const marketplaceRoute = isDonation ? MARKETPLACE_DONATE_ROUTE : MARKETPLACE_ITEMS_ROUTE;
      dispatch(setCountryId(newCountryId));
      go(`${parentUrl}/marketplace/${marketplaceRoute}`);
      trackEvent('Product - Clicked exchange through country link');
    },
    [parentUrl, isDonation, go, trackEvent, dispatch],
  );

  const handleSelectShippingAddress = useCallback(
    (addressOption: AddressOption) => {
      selectShippingAddress(addressOption);
      trackEvent('Address verification — address selected', { type: addressOption, productId });
      goNextStep();
    },
    [goNextStep, trackEvent, selectShippingAddress, productId],
  );

  useScrollTop();

  usePageLoadEvent('Shipping page - Opened');

  useEffect(() => {
    setActiveStatus(GIFT_FLOW_SHIPPING_ID);
  }, [setActiveStatus]);

  useEffect(() => {
    if (shouldGoToTheNextStep) {
      saveShippingAddress();
      trackEvent('Address - Submitted address and continue', { productId });
      goNextStep();
    }
  }, [shouldGoToTheNextStep, goNextStep, trackEvent, productId, saveShippingAddress]);

  useEffect(() => {
    if (hashId && countryId && !shipping) {
      dispatch(fetchShippingAddress.pending({ hashId, countryId }));
    }
  }, [hashId, countryId, shipping, dispatch]);

  return (
    <RecipientActionWrapper
      titleContent={<PageTitle title="" subTitle="Shipping Address" flipStyles />}
      titleWrapperProps={{ minHeight: 120 }}
    >
      {addressFlow === ShippingAddressFlow.shippingForm && (
        <Box width={[320, '100%', 484]}>
          <Alert icon={<></>}>
            Please note <b>we cannot ship to PO boxes</b>. Please enter a physical address to ensure your gift gets
            delivered properly.
          </Alert>
        </Box>
      )}
      <Box width={[320, '100%', 484]}>
        {recipient && <RecipientInfo email={recipient.email} fullName={recipient.fullName} />}
        {addressFlow === ShippingAddressFlow.shippingForm && (
          <ShippingAddressForm
            onSubmit={submitShippingAddress}
            onResetErrors={resetFieldError}
            onChangeProduct={handleBackToMarketplace}
          />
        )}
        {addressFlow === ShippingAddressFlow.addressSelectionForm && (
          <AddressSelectionForm onSubmit={handleSelectShippingAddress} />
        )}
      </Box>
    </RecipientActionWrapper>
  );
};

export default ShippingPage;
